import { some } from 'lodash';

import { DPSuggestionKinds } from '@/modules/aiDP/context/dpFormSuggestions.context';
import {
  AIDP_SUPPORTED_TRUST_ENTITY_TYPES,
  WILL_GOVERNED_ACCOUNT_DP_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { EntityType } from '@/modules/entities/types/EntityType';
// eslint-disable-next-line luminary-rules/no-page-imports
import {
  ManageDispositions_AiSuggestionFragment,
  ManageDispositions_EntityFragment,
  ManageDispositions_TestamentaryEntityFragment,
} from '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/graphql/ManageDispositionsPage.generated';
import { AiSuggestionKind } from '@/types/schema';

export function isAIDPWorkflowEnabled(
  entityType: EntityType | null | undefined | ''
) {
  if (!entityType) return false;

  if (WILL_GOVERNED_ACCOUNT_DP_ENTITY_TYPES.includes(entityType)) {
    return true;
  }

  if (AIDP_SUPPORTED_TRUST_ENTITY_TYPES.includes(entityType)) {
    return true;
  }

  return false;
}

/**
 * Determines if an entity has unacknowledged AI DP suggestions by traversing
 * the input entity -> kgroot -> ai suggestions, or by checking the map of
 * AI suggestions by entity ID.
 * @param entity
 * @param dpSuggestionKinds
 * @param dpAISuggestionsByEntityOrTEID
 * @param firstDeathClientId
 */
export function doesEntityHaveUnackedDPSuggestions(
  entity: ManageDispositions_EntityFragment,
  dpSuggestionKinds: DPSuggestionKinds | undefined,
  dpAISuggestionsByEntityOrTEID: Record<
    string,
    ManageDispositions_AiSuggestionFragment[]
  >,
  firstDeathClientId?: string
): Partial<Record<AiSuggestionKind, boolean>> {
  const aiSuggestions = dpAISuggestionsByEntityOrTEID?.[entity.id] || [];

  const hasGrantor1Suggestions = hasUnacknowledgedDPSuggestions(
    aiSuggestions,
    AiSuggestionKind.DpRecipientGrantor_1DiesFirst
  );
  const hasGrantor2Suggestions = hasUnacknowledgedDPSuggestions(
    aiSuggestions,
    AiSuggestionKind.DpRecipientGrantor_2DiesFirst
  );
  const hasV2Suggestions =
    !!firstDeathClientId &&
    hasUnacknowledgedDPSuggestionsForFirstDeathGrantor(
      aiSuggestions,
      firstDeathClientId
    );

  if (!dpSuggestionKinds) {
    return {
      [AiSuggestionKind.DpRecipientGrantor_1DiesFirst]: hasGrantor1Suggestions,
      [AiSuggestionKind.DpRecipientGrantor_2DiesFirst]: hasGrantor2Suggestions,
      ...(firstDeathClientId
        ? { [AiSuggestionKind.DpRecipientV2]: hasV2Suggestions }
        : {}),
    };
  }

  return {
    [AiSuggestionKind.DpRecipientGrantor_1DiesFirst]: hasGrantor1Suggestions,
    [AiSuggestionKind.DpRecipientGrantor_2DiesFirst]: hasGrantor2Suggestions,
    ...(firstDeathClientId
      ? { [AiSuggestionKind.DpRecipientV2]: hasV2Suggestions }
      : {}),
  };
}

/**
 * Determines if a testamentary entity has unacknowledged AI DP suggestions by
 * traversing the input te -> kgroot -> ai suggestions
 * @param te
 * @param dpSuggestionKinds
 * @param dpAISuggestionsByEntityOrTEID
 * @param firstDeathClientId
 */
export function doesTestamentaryEntityHaveUnackedDPSuggestions(
  te: ManageDispositions_TestamentaryEntityFragment,
  dpSuggestionKinds: DPSuggestionKinds | undefined,
  dpAISuggestionsByEntityOrTEID: Record<
    string,
    ManageDispositions_AiSuggestionFragment[]
  >,
  _firstDeathClientId?: string
): Partial<Record<AiSuggestionKind, boolean>> {
  const aiSuggestions = dpAISuggestionsByEntityOrTEID?.[te.id] || [];

  const hasGrantor1Suggestions = hasUnacknowledgedDPSuggestions(
    aiSuggestions,
    AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst
  );
  const hasGrantor2Suggestions = hasUnacknowledgedDPSuggestions(
    aiSuggestions,
    AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst
  );
  // For TE rows, surface V2 suggestions regardless of selected first-death
  // client so sparkles are not hidden by the active death-order tab.
  const hasV2Suggestions = hasUnacknowledgedDPSuggestions(
    aiSuggestions,
    AiSuggestionKind.DpRecipientV2
  );

  if (!dpSuggestionKinds) {
    return {
      [AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst]:
        hasGrantor1Suggestions,
      [AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst]:
        hasGrantor2Suggestions,
      [AiSuggestionKind.DpRecipientV2]: hasV2Suggestions,
    };
  }

  return {
    [AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst]: hasGrantor1Suggestions,
    [AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst]: hasGrantor2Suggestions,
    [AiSuggestionKind.DpRecipientV2]: hasV2Suggestions,
  };
}

/**
 * Determines if there are unacknowledged AI DP suggestions of a specific kind
 * in the provided list of AI suggestions.
 * @param aiSuggestions
 * @param kind
 */
export function hasUnacknowledgedDPSuggestions(
  aiSuggestions: ManageDispositions_AiSuggestionFragment[],
  kind: AiSuggestionKind
): boolean {
  return some(
    aiSuggestions,
    (suggestion) =>
      suggestion.acceptanceStatus === null && suggestion.kind === kind
  );
}

function countUnacknowledgedDPSuggestions(
  aiSuggestions: ManageDispositions_AiSuggestionFragment[],
  kind: AiSuggestionKind,
  firstDeathClientId?: string
): number {
  return aiSuggestions.filter((suggestion) => {
    if (suggestion.acceptanceStatus !== null || suggestion.kind !== kind) {
      return false;
    }

    if (kind === AiSuggestionKind.DpRecipientV2 && firstDeathClientId) {
      return (
        suggestion.dpFirstDeathGrantorClientProfile?.id === firstDeathClientId
      );
    }

    return true;
  }).length;
}

/**
 * Counts unacknowledged DP suggestions for a target row, scoped to the active
 * first-death grantor for V2 suggestions. Falls back to the active legacy kind
 * when the feature-flag-selected suggestion map contains V1 data instead.
 */
export function countDPSuggestionsForTarget(
  targetId: string,
  dpAISuggestionsByEntityOrTEID: Record<
    string,
    ManageDispositions_AiSuggestionFragment[]
  >,
  activeClientDeathId: string | undefined,
  dpSuggestionKinds: DPSuggestionKinds | undefined
): number {
  const aiSuggestions = dpAISuggestionsByEntityOrTEID[targetId] || [];
  const v2Count = activeClientDeathId
    ? countUnacknowledgedDPSuggestions(
        aiSuggestions,
        AiSuggestionKind.DpRecipientV2,
        activeClientDeathId
      )
    : 0;

  if (v2Count > 0) {
    return v2Count;
  }

  if (dpSuggestionKinds) {
    return countUnacknowledgedDPSuggestions(
      aiSuggestions,
      dpSuggestionKinds.aiSuggestionKind
    );
  }

  return 0;
}

function hasUnacknowledgedDPSuggestionsForFirstDeathGrantor(
  aiSuggestions: ManageDispositions_AiSuggestionFragment[],
  firstDeathClientId: string
): boolean {
  return some(
    aiSuggestions,
    (suggestion) =>
      suggestion.acceptanceStatus === null &&
      suggestion.kind === AiSuggestionKind.DpRecipientV2 &&
      suggestion.dpFirstDeathGrantorClientProfile?.id === firstDeathClientId
  );
}

/**
 * Returns a new suggestionsByKind map with the DpRecipientV2 entry filtered
 * to only suggestions whose first-death grantor matches the given client ID.
 * Other kinds are passed through unchanged. Returns the input unchanged if
 * there are no V2 suggestions present.
 *
 * Used to scope the consolidated dispositions count to a single death-order
 * scenario (e.g. the primary death order) so the count does not include
 * suggestions from a hidden death-order scenario.
 *
 * @param suggestionsByKind
 * @param firstDeathClientId
 */
export function scopeDpV2SuggestionsToFirstDeathClient<
  T extends {
    dpFirstDeathGrantorClientProfile?: { id: string } | null;
  },
>(
  suggestionsByKind: Partial<Record<AiSuggestionKind, T[]>>,
  firstDeathClientId: string
): Partial<Record<AiSuggestionKind, T[]>> {
  const v2Suggestions = suggestionsByKind[AiSuggestionKind.DpRecipientV2];
  if (!v2Suggestions) return suggestionsByKind;
  return {
    ...suggestionsByKind,
    [AiSuggestionKind.DpRecipientV2]: v2Suggestions.filter(
      (s) => s.dpFirstDeathGrantorClientProfile?.id === firstDeathClientId
    ),
  };
}
